import React, { useEffect, useRef, useState } from 'react';
import './Receipt.css';
import storeService from '../../services/storeService';
import { Capacitor } from '@capacitor/core';
import { getPreferredPrinterConfig } from '../../utils/printerPreferences';
import { formatInStoreTimezone } from '../../utils/dateUtils';

const formatMoney = (value = 0) => Number(value || 0).toFixed(2);

const resolveTyroReceiptCopies = (receipts) => {
  if (!receipts) return [];
  if (Array.isArray(receipts.copies) && receipts.copies.length > 0) {
    return receipts.copies.filter((copy) => copy?.customerReceipt || copy?.merchantReceipt);
  }
  if (receipts.customerReceipt || receipts.merchantReceipt) {
    return [
      {
        customerReceipt: receipts.customerReceipt || '',
        merchantReceipt: receipts.merchantReceipt || '',
        tyroTransactionId: receipts.tyroTransactionId || undefined,
      },
    ];
  }
  return [];
};

const RefundReceiptModal = ({ refund, originalSale, store: initialStore, onClose }) => {
  const [storeDetails, setStoreDetails] = useState(initialStore);
  const receiptRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const loadStore = async () => {
      if (initialStore) return;
      const storeId = refund?.store || originalSale?.store;
      if (!storeId) return;
      try {
        const storeData = await storeService.getStoreById(storeId);
        if (isMounted) {
          setStoreDetails(storeData);
        }
      } catch (err) {
        console.error('Failed to load store for refund receipt', err);
      }
    };

    loadStore();
    return () => {
      isMounted = false;
    };
  }, [initialStore, refund, originalSale]);

  if (!refund) return null;

  const receiptStore = storeDetails || (typeof originalSale?.store === 'object' ? originalSale.store : null);
  const timezone = receiptStore?.timezone || 'Australia/Melbourne';
  const address = receiptStore?.address;
  const formattedAddress = address
    ? [address.street, address.suburb, address.state, address.postcode].filter(Boolean).join(', ')
    : '';
  const tyroReceipts = refund.tyroReceipts;
  const tyroReceiptCopies = resolveTyroReceiptCopies(tyroReceipts);
  const cashierUserId = originalSale?.user?.userId || '';
  const registerName = originalSale?.till?.name || '';

  const buildPrintableHtml = () => {
    if (!receiptRef.current) return null;
    const receiptMarkup = receiptRef.current.outerHTML;
    const headContent = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Refund Receipt</title>
          ${headContent}
          <style>
            body {
              margin: 0;
              padding: 12px;
              background: #fff;
            }
            #receipt-to-print {
              width: 288px;
              margin: 0 auto;
            }
          </style>
        </head>
        <body>
          ${receiptMarkup}
        </body>
      </html>
    `;
  };

  const getPrinterPlugin = () => {
    if (!Capacitor.isPluginAvailable('ReceiptPrinter')) {
      return null;
    }
    return window?.Capacitor?.Plugins?.ReceiptPrinter || null;
  };

  const buildTyroPrintableMarkup = ({ title, sections }) => {
    const sectionMarkup = sections
      .filter(({ text }) => !!text)
      .map(
        ({ heading, text }) => `
          <div style="margin-bottom: 16px;">
            <div style="font-weight: 700; font-size: 12px; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 6px;">
              ${heading}
            </div>
            <pre style="white-space: pre-wrap; font-size: 12px; line-height: 1.4; margin: 0;">${text}</pre>
          </div>
        `
      )
      .join('\n');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body {
              margin: 0;
              font-family: "Courier New", Courier, monospace;
              background: #fff;
              padding: 12px;
              width: 288px;
            }
            @page { margin: 8px; }
          </style>
        </head>
        <body>
          ${sectionMarkup}
        </body>
      </html>
    `;
  };

  const tryNativePrint = async (html) => {
    const plugin = getPrinterPlugin();
    if (!plugin) return false;
    try {
      const printerConfig = getPreferredPrinterConfig('receipt');
      const payload = { html, jobType: 'receipt' };
      if (printerConfig.transport === 'network' && printerConfig.host) {
        payload.networkHost = printerConfig.host;
        if (printerConfig.port) payload.networkPort = printerConfig.port;
      } else if (printerConfig.bluetoothName) {
        payload.bluetoothNameHint = printerConfig.bluetoothName;
      }
      await plugin.print(payload);
      return true;
    } catch (err) {
      console.error('[RefundReceiptModal] Native refund print failed', err);
      return false;
    }
  };

  const handlePrint = async () => {
    const printableHtml = buildPrintableHtml();
    if (!printableHtml) {
      window.print();
      return;
    }

    if (await tryNativePrint(printableHtml)) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=420,height=800');
    if (!printWindow) {
      window.print();
      return;
    }

    printWindow.document.write(printableHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);
  };

  const printTyroReceipt = async (receiptText, label) => {
    if (!receiptText) return;
    const plugin = getPrinterPlugin();
    const printableMarkup = buildTyroPrintableMarkup({
      title: label,
      sections: [{ heading: label, text: receiptText }],
    });

    if (plugin) {
      try {
        const printerConfig = getPreferredPrinterConfig('receipt');
        const payload = { html: printableMarkup, jobType: 'receipt' };
        if (printerConfig.transport === 'network' && printerConfig.host) {
          payload.networkHost = printerConfig.host;
          if (printerConfig.port) {
            payload.networkPort = printerConfig.port;
          }
        } else if (printerConfig.bluetoothName) {
          payload.bluetoothNameHint = printerConfig.bluetoothName;
        }
        await plugin.print(payload);
        return;
      } catch (err) {
        console.error('[RefundReceiptModal] Tyro copy native print failed', err);
      }
    }

    const printWindow = window.open('', '_blank', 'width=420,height=800');
    if (!printWindow) {
      window.alert('Unable to open print preview. Please allow popups.');
      return;
    }
    printWindow.document.write(printableMarkup);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);
  };

  const printCombinedTyroReceipts = async (receipts) => {
    const receiptCopies = resolveTyroReceiptCopies(receipts);
    if (!receiptCopies.length) return;
    const printableMarkup = buildTyroPrintableMarkup({
      title: 'EFTPOS Receipts',
      sections: receiptCopies.flatMap((copy, index) => [
        {
          heading: `Split leg ${index + 1} - Customer copy${
            copy?.tyroTransactionId ? ` (${copy.tyroTransactionId})` : ''
          }`,
          text: copy.customerReceipt,
        },
        {
          heading: `Split leg ${index + 1} - Merchant copy${
            copy?.tyroTransactionId ? ` (${copy.tyroTransactionId})` : ''
          }`,
          text: copy.merchantReceipt,
        },
      ]),
    });
    const plugin = getPrinterPlugin();
    if (plugin) {
      try {
        const printerConfig = getPreferredPrinterConfig('receipt');
        const payload = { html: printableMarkup, jobType: 'receipt' };
        if (printerConfig.transport === 'network' && printerConfig.host) {
          payload.networkHost = printerConfig.host;
          if (printerConfig.port) payload.networkPort = printerConfig.port;
        } else if (printerConfig.bluetoothName) {
          payload.bluetoothNameHint = printerConfig.bluetoothName;
        }
        await plugin.print(payload);
        return;
      } catch (err) {
        console.error('[RefundReceiptModal] Tyro combined print failed', err);
      }
    }

    const printWindow = window.open('', '_blank', 'width=420,height=800');
    if (!printWindow) {
      window.alert('Unable to open print preview. Please allow popups.');
      return;
    }
    printWindow.document.write(printableMarkup);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 200);
  };

  const renderTyroReceiptPreview = (label, receiptText) => (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs tracking-[0.3em] uppercase text-slate-500">{label}</p>
        <button
          onClick={() => printTyroReceipt(receiptText, `${label} Refund Receipt`)}
          className="text-xs font-semibold text-primary hover:text-primary-dark"
        >
          Print
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto rounded-xl bg-white border border-slate-200 p-3">
        <pre className="text-[11px] whitespace-pre-wrap text-slate-700">{receiptText}</pre>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm max-h-[calc(100vh-2rem)] overflow-y-auto flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Refund</p>
            <h2 className="text-lg font-semibold text-slate-900">Refund Receipt</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handlePrint}
              className="bg-gray-600 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-700"
            >
              Print
            </button>
            {tyroReceiptCopies.length > 0 && (
              <button
                onClick={() => printCombinedTyroReceipts(tyroReceipts)}
                className="bg-secondary text-white px-3 py-2 rounded-md text-sm hover:bg-secondary-dark"
              >
                Print EFTPOS
              </button>
            )}
            <button
              onClick={onClose}
              className="bg-primary text-white px-4 py-2 rounded-md text-sm hover:bg-primary-dark"
            >
              Close
            </button>
          </div>
        </div>

        <div className="max-h-[75vh] overflow-y-auto">
          <div ref={receiptRef} id="receipt-to-print" className="text-gray-900 p-6 font-mono text-sm bg-white leading-snug">
          <div className="text-center space-y-1">
            {receiptStore?.name && <h1 className="text-lg font-bold tracking-wide">{receiptStore.name}</h1>}
            {receiptStore?.companyName && <p className="text-xs uppercase">{receiptStore.companyName}</p>}
            {formattedAddress && <p className="text-xs">{formattedAddress}</p>}
            {receiptStore?.phone && <p className="text-xs">Ph: {receiptStore.phone}</p>}
          </div>

          <div className="text-center my-4">
            <p className="text-base font-bold">Refund Receipt</p>
          </div>

          <div className="border-t border-b border-dashed border-gray-400 my-3 py-2 text-xs space-y-1">
            <div className="text-xs font-mono whitespace-nowrap">
              Time: {formatInStoreTimezone(refund.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}
            </div>
            <div className="flex justify-between">
              <span>Original Receipt #:</span>
              <span>{refund.receiptNumber}</span>
            </div>
            {originalSale?.paymentMethod && (
              <div className="flex justify-between">
                <span>Original Payment:</span>
                <span>{originalSale.paymentMethod}</span>
              </div>
            )}
            {cashierUserId && (
              <div className="flex justify-between">
                <span>User ID:</span>
                <span>{cashierUserId}</span>
              </div>
            )}
            {registerName && (
              <div className="flex justify-between">
                <span>POS:</span>
                <span>{registerName}</span>
              </div>
            )}
            {refund.tyroTransactionId && (
              <div className="flex justify-between">
                <span>Tyro Txn ID:</span>
                <span>{refund.tyroTransactionId}</span>
              </div>
            )}
          </div>

          <div>
            <div className="flex text-xs font-semibold uppercase border-b border-gray-300 pb-1">
              <span className="flex-1">Item Name</span>
              <span className="w-12 text-right">Qty</span>
              <span className="w-20 text-right">Amount($)</span>
            </div>
            <div className="divide-y divide-dashed divide-gray-300">
              {(refund.refundedItems || []).map((item, idx) => (
                <div key={`${item.product}-${item.saleItemIndex ?? idx}-${idx}`} className="flex py-1 text-xs">
                  <span className="flex-1 pr-2">{item.name}</span>
                  <span className="w-12 text-right">{item.quantity}</span>
                  <span className="w-20 text-right">${formatMoney(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 space-y-1 text-xs">
            <div className="flex justify-between font-bold text-base border-t border-dashed border-gray-400 pt-2">
              <span>Total Refund</span>
              <span>${formatMoney(refund.refundAmount)}</span>
            </div>
            {refund.reason && (
              <div className="pt-2 text-xs">
                <span className="font-semibold">Reason:</span>
                <span className="ml-1">{refund.reason}</span>
              </div>
            )}
          </div>

        </div>

        {tyroReceiptCopies.length > 0 && (
          <div className="px-6 pt-4 pb-2 space-y-4 bg-white border-t border-b border-gray-100">
            <p className="text-xs uppercase tracking-[0.35em] text-slate-500">Tyro EFTPOS receipts</p>
            {tyroReceiptCopies.map((copy, index) => (
              <div key={`refund-tyro-copy-${index}`} className="space-y-3">
                <p className="text-xs text-slate-500">
                  {`Split leg ${index + 1}${copy?.tyroTransactionId ? ` • Txn ${copy.tyroTransactionId}` : ''}`}
                </p>
                {copy.customerReceipt && renderTyroReceiptPreview(`Customer copy (Leg ${index + 1})`, copy.customerReceipt)}
                {copy.merchantReceipt && renderTyroReceiptPreview(`Merchant copy (Leg ${index + 1})`, copy.merchantReceipt)}
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default RefundReceiptModal;
